/**
 * Вкладка "Потенциальные семьи": отображает дубликаты телефонов
 * Дата: 09.01.2026 - Создан для TODO-029 (Patient Families)
 * Связан с: frontend/src/pages/PatientFamilies.tsx
 */
import { useState } from 'react';
import { Button, Badge, Input, Modal, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui';
import { useDuplicates, useCreateFamilyFromDuplicates } from '@/hooks/useFamilies';
import { DuplicatePhoneGroup } from '@/api/types';
import { Can } from '@/components/Can';

interface DuplicatesTabProps {
  tenantId?: string;
}

export function DuplicatesTab({ tenantId }: DuplicatesTabProps) {
  const { data, isLoading } = useDuplicates(tenantId);
  const createFamily = useCreateFamilyFromDuplicates();
  const [selectedGroup, setSelectedGroup] = useState<DuplicatePhoneGroup | null>(null);
  const [familyName, setFamilyName] = useState('');
  const [primaryPatientId, setPrimaryPatientId] = useState('');
  const [largeGroupChecked, setLargeGroupChecked] = useState(false);

  const duplicates = data?.duplicates || [];

  const handleCreateFamily = () => {
    if (!selectedGroup || !familyName || !primaryPatientId) return;

    createFamily.mutate({
      data: {
        phone: selectedGroup.phone_normalized,
        name: familyName,
        primary_patient_id: primaryPatientId,
      },
      tenantId,
    }, {
      onSuccess: () => {
        setSelectedGroup(null);
        setFamilyName('');
        setPrimaryPatientId('');
        setLargeGroupChecked(false);
      },
    });
  };

  if (isLoading) {
    return <div className="text-center py-8 text-neutral-500">Загрузка...</div>;
  }

  if (duplicates.length === 0) {
    return (
      <div className="text-center py-8 text-neutral-500">
        Нет номеров телефонов с несколькими пациентами.
      </div>
    );
  }

  const openReview = (group: DuplicatePhoneGroup) => {
    setSelectedGroup(group);
    setFamilyName(`Семья ${group.phone_normalized.slice(-4)}`);
    setPrimaryPatientId(group.patients[0]?.id || '');
    setLargeGroupChecked(false);
  };
  const needsDataReview = (count: number) => count >= 8;
  const confirmBlocked = Boolean(selectedGroup && needsDataReview(selectedGroup.count) && !largeGroupChecked);

  return (
    <div className="space-y-3">
      <p className="text-sm leading-5 text-shell-muted">
        {duplicates.length} номеров встречаются у нескольких карт. Общий телефон ещё не семья: состав нужно проверить перед подтверждением.
      </p>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Телефон</TableHeader>
            <TableHeader>Карт</TableHeader>
            <TableHeader>Основание</TableHeader>
            <TableHeader>Проверка</TableHeader>
            <TableHeader>Действие</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {duplicates.map((group) => (
            <TableRow key={group.phone_normalized}>
              <TableCell className="font-medium">{group.phone_normalized}</TableCell>
              <TableCell>{group.count}</TableCell>
              <TableCell>Совпадение номера</TableCell>
              <TableCell>
                {needsDataReview(group.count) ? (
                  <Badge variant="warning">Нужна проверка данных</Badge>
                ) : (
                  <Badge variant="secondary">Кандидат</Badge>
                )}
              </TableCell>
              <TableCell>
                <Can permission="families:write">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => openReview(group)}
                    testId={`btn-create-family-from-${group.phone_normalized}`}
                  >
                    Проверить
                  </Button>
                </Can>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Модалка создания семьи */}
      {selectedGroup && (
        <Modal
          isOpen={!!selectedGroup}
          onClose={() => setSelectedGroup(null)}
          title="Проверка перед семьёй"
        >
          <div className="space-y-4">
            <p className="text-sm leading-5 text-shell-muted">
              Общий номер не объединяет карты в семью и не открывает медкарту. Роли по фамилии не назначаются. Подтверждение записывается как решение создать семью.
            </p>
            {needsDataReview(selectedGroup.count) && (
              <p className="rounded-lg bg-shell-warning-soft px-3 py-2 text-sm text-shell-warning">
                На номере {selectedGroup.count} карт. Такой номер бывает общим, тестовым или ошибочным. Номера сами не исправляются.
              </p>
            )}
            <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-shell-border p-2">
              {selectedGroup.patients.map((patient) => (
                <p key={patient.id} className="text-sm text-shell-text">
                  {patient.full_name || `Пациент ${patient.id.slice(0, 8)}`}
                </p>
              ))}
            </div>
            <Input
              label="Название семьи"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="Например: Семья Гаджиевых"
              required
            />

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Основной контакт (получает уведомления)
              </label>
              <select
                value={primaryPatientId}
                onChange={(e) => setPrimaryPatientId(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {selectedGroup.patients.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.full_name || `Пациент ${patient.id.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>

            {needsDataReview(selectedGroup.count) && (
              <label className="flex items-start gap-2 text-sm text-shell-text">
                <input
                  type="checkbox"
                  checked={largeGroupChecked}
                  onChange={(event) => setLargeGroupChecked(event.target.checked)}
                  className="mt-1"
                />
                Я проверил список и подтверждаю, что это одна семья.
              </label>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setSelectedGroup(null)}>
                Отмена
              </Button>
              <Button
                onClick={handleCreateFamily}
                disabled={!familyName || !primaryPatientId || createFamily.isPending || confirmBlocked}
                testId="btn-confirm-create-family"
              >
                Создать
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

